import { ResultOf, readFragment } from "gql.tada";
import { ProductMotionSlidesSectionById } from "@queries/entries/ProductMotionSlidesSectionById";
import { useId, useRef } from "react";
import { useScroll, useTransform, motion } from "motion/react";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { AnimatedSplitLines } from "@components/shared/AnimatedSplitLines";
import { Button } from "@components/shared/core/Button";
import { Link } from "@components/shared/core/Link";
import cx from "classnames";
import chunk from "lodash/chunk";
import { RandomTwistFrame } from "./RandomTwistFrame";

const EXTRA_SPACING_SCREENS = 1.3;

type Slide = NonNullable<
  NonNullable<
    NonNullable<
      ResultOf<
        typeof ProductMotionSlidesSectionById
      >["productMotionSlidesSection"]
    >["slidesCollection"]
  >["items"][number]
>;

export const ProductMotionInitialSlide = ({
  entry,
}: {
  entry: NonNullable<
    ResultOf<
      typeof ProductMotionSlidesSectionById
    >["productMotionSlidesSection"]
  >;
}) => {
  const slideRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: slideRef,
    offset: ["start end", "end start"],
  });
  const evenValue = useTransform(
    scrollYProgress,
    [0, 1 / 3, 2 / 3, 1],
    [1, 0, 0, -1]
  );
  const translateYSlowScroll = useTransform(
    scrollYProgress,
    [0, 1 / 3, 2 / 3, 1],
    [50, 50, -50, -50]
  );

  const superTitleOpacity = useTransform(
    scrollYProgress,
    [0, 0.25, 0.4, 0.6, 1],
    [0, 0, 1, 1, 0]
  );

  return (
    <div
      className="w-full"
      style={{ height: `${EXTRA_SPACING_SCREENS * 100}vh` }}
      ref={slideRef}
    >
      <motion.div
        className="h-screen w-full sticky top-0 flex justify-center items-center text-brand-cream"
        style={{ y: translateYSlowScroll }}
      >
        <div className="h-screen flex justify-center items-center">
          <div className="flex flex-col gap-16 max-w-screen-xl">
            <motion.p
              className="text-body-small pb-14 text-center"
              style={{
                opacity: superTitleOpacity,
              }}
            >
              {entry.superTitle}
            </motion.p>
            <p className="text-display text-center text-balance">
              <AnimatedSplitLines center value={evenValue}>
                {entry.title ?? ""}
              </AnimatedSplitLines>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export const ProductMotionSlide = ({
  slide,
  index,
  id,
}: {
  slide: Slide;
  index: number;
  id: string;
}) => {
  const slideRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const { scrollYProgress } = useScroll({
    target: slideRef,
    offset: ["start end", "end start"],
  });
  const evenValue = useTransform(
    scrollYProgress,
    [0, 1 / 3, 2 / 3, 1],
    [1, 0, 0, -1]
  );
  const bottomSectionY = useTransform(
    scrollYProgress,
    [0, 1 / 3, 2 / 3, 1],
    [200, 0, 0, -200]
  );
  const bottomSectionOpacity = useTransform(
    scrollYProgress,
    Array.from({ length: 7 }, (_, index) => index / 6),
    [0, 0, 1, 1, 1, 0, 0]
  );

  const translateYSlowScroll = useTransform(
    scrollYProgress,
    [0, 1 / 3, 2 / 3, 1],
    [50, 50, -50, -50]
  );

  const ctaLink = readFragment(LinkFragment, slide.ctaLink);

  return (
    <div
      ref={slideRef}
      className="w-full"
      style={{ height: `${EXTRA_SPACING_SCREENS * 100}vh` }}
      id={id}
    >
      <motion.div
        style={{ y: translateYSlowScroll }}
        className="h-screen w-full flex justify-center items-center sticky top-0"
      >
        <div className="h-screen flex justify-center items-center flex-col gap-12 relative w-full">
          <p className="text-display dsk:max-w-[75%]">
            <AnimatedSplitLines center value={evenValue} id={titleId}>
              {slide?.title ?? ""}
            </AnimatedSplitLines>
          </p>
          <motion.div
            className="max-w-sm flex flex-col gap-8"
            style={{ opacity: bottomSectionOpacity, y: bottomSectionY }}
          >
            <p className="text-center">{slide.subtitle}</p>
            {!!ctaLink && (
              <div className="flex justify-center">
                <Button asChild size="large" variant="light-on-blur">
                  <Link link={ctaLink} aria-labelledby={titleId}>
                    {ctaLink.text}
                  </Link>
                </Button>
              </div>
            )}
          </motion.div>
          <div
            className={cx(
              "absolute inset-0 -z-20 flex justify-between h-full w-[calc(100%+36px)] dsk:w-[calc(100%+64px)] overflow-x-clip -mx-[18px] dsk:-mx-[32px]",
              index % 2 === 0
                ? "flex-col dsk:flex-row"
                : "flex-col-reverse dsk:flex-row-reverse"
            )}
          >
            {chunk(slide.mediaAssetsCollection?.items ?? [], 2).map(
              (chunk, chunkIndex) => (
                <div
                  key={chunkIndex}
                  className="w-full h-1/2 dsk:w-1/2 dsk:h-full flex flex-row dsk:flex-col"
                >
                  {chunk.map((media, index) => (
                    <div
                      key={index}
                      className="grow w-1/2 dsk:size-full flex justify-center items-center"
                    >
                      <RandomTwistFrame value={evenValue}>
                        <img
                          src={media?.url ?? ""}
                          aria-hidden
                          alt=""
                          className="rounded-xl max-w-[223px] max-h-[122px] dsk:max-w-[332px] dsk:max-h-[187px] brightness-80"
                        />
                      </RandomTwistFrame>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};
